"use client";

import { useEffect, useState, useCallback, useRef } from "react";

interface Slide {
  src: string;
  boutique: string;
  tagline: string;
  accent: string; // small icon glyph from the boutique grid
}

const SLIDES: Slide[] = [
  {
    src: "/hero-images/01-frais-marche.png",
    boutique: "Frais & Marché",
    tagline: "Le Terroir Français · produits du jour",
    accent: "❖",
  },
  {
    src: "/hero-images/02-epicerie-fine.png",
    boutique: "Épicerie Fine",
    tagline: "Caviar · Truffe · Champagne",
    accent: "✦",
  },
  {
    src: "/hero-images/03-beaute-parfums.png",
    boutique: "Beauté & Parfums",
    tagline: "Grands parfumeurs · coffrets prestige",
    accent: "❀",
  },
  {
    src: "/hero-images/04-hightech-electro.png",
    boutique: "High-Tech & Électro",
    tagline: "Galerie numérique · titanium & or",
    accent: "◈",
  },
  {
    src: "/hero-images/05-cave-exception.png",
    boutique: "Cave d'Exception",
    tagline: "800 références · Bordeaux & Bourgogne",
    accent: "❦",
  },
];

const AUTOPLAY_MS = 5500;

export function HeroSlider() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number>(0);
  const total = SLIDES.length;

  const next = useCallback(
    () => setActive((v) => (v + 1) % total),
    [total]
  );
  const prev = useCallback(
    () => setActive((v) => (v - 1 + total) % total),
    [total]
  );
  const goTo = useCallback((i: number) => setActive(i), []);

  // Reset progress whenever the active slide changes.
  useEffect(() => {
    setProgress(0);
    startRef.current = performance.now();
  }, [active]);

  // RAF loop to drive the progress bar smoothly.
  useEffect(() => {
    if (paused) {
      // Freeze progress; on resume, rebase startRef so elapsed picks up correctly.
      const frozenAt = progress;
      return () => {
        startRef.current = performance.now() - frozenAt * AUTOPLAY_MS;
      };
    }
    const tick = (now: number) => {
      const elapsed = now - startRef.current;
      const pct = Math.min(1, elapsed / AUTOPLAY_MS);
      setProgress(pct);
      if (pct >= 1) {
        setActive((v) => (v + 1) % total);
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, paused, total]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  return (
    <div
      className="relative w-full h-full overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      role="region"
      aria-roledescription="carousel"
      aria-label="Sélection de boutiques de luxe Hyper OR I"
    >
      {/* Slides — absolutely stacked, opacity crossfade */}
      {SLIDES.map((s, i) => (
        <div
          key={s.src}
          className={`absolute inset-0 transition-opacity duration-[1200ms] ease-out ${
            i === active
              ? "opacity-100"
              : "opacity-0 pointer-events-none"
          }`}
          aria-hidden={i !== active}
        >
          <img
            src={s.src}
            alt={`${s.boutique} — ${s.tagline}`}
            className="absolute inset-0 h-full w-full object-cover"
            loading={i === 0 ? "eager" : "lazy"}
            decoding="async"
            draggable={false}
          />
          {/* Dark gradient overlays for legibility + brand mood */}
          <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/65 to-ink/30"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-ink/40"></div>
        </div>
      ))}

      {/* Caption block — bottom-left, transitions with slide */}
      <div className="absolute bottom-10 md:bottom-14 left-6 md:left-10 z-10 max-w-md">
        {SLIDES.map((s, i) => (
          <div
            key={s.src}
            className={`transition-all duration-700 ${
              i === active
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-3 absolute pointer-events-none"
            }`}
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="text-gold text-2xl leading-none">
                {s.accent}
              </span>
              <span className="text-gold text-[11px] tracking-[0.35em] uppercase">
                {s.boutique}
              </span>
            </div>
            <p className="font-serif italic text-cream/85 text-base md:text-lg leading-snug">
              {s.tagline}
            </p>
          </div>
        ))}
      </div>

      {/* Arrows — desktop */}
      <button
        onClick={prev}
        aria-label="Slide précédent"
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 h-11 w-11 items-center justify-center rounded-full border border-cream/25 text-cream/70 hover:border-gold hover:text-gold hover:bg-ink/40 transition-all backdrop-blur-sm"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="m15 18-6-6 6-6"></path>
        </svg>
      </button>
      <button
        onClick={next}
        aria-label="Slide suivant"
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 h-11 w-11 items-center justify-center rounded-full border border-cream/25 text-cream/70 hover:border-gold hover:text-gold hover:bg-ink/40 transition-all backdrop-blur-sm"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="m9 18 6-6-6-6"></path>
        </svg>
      </button>

      {/* Dots — bottom-right */}
      <div className="absolute bottom-10 md:bottom-14 right-6 md:right-10 z-10 flex items-center gap-2.5">
        {SLIDES.map((s, i) => (
          <button
            key={s.src}
            onClick={() => goTo(i)}
            aria-label={`Aller au slide ${i + 1} · ${s.boutique}`}
            aria-current={i === active}
            className={`group relative h-2 transition-all duration-500 ${
              i === active
                ? "w-10 bg-gold"
                : "w-2 bg-cream/40 hover:bg-cream/70"
            } rounded-full`}
          >
            <span className="sr-only">{i + 1}</span>
          </button>
        ))}
      </div>

      {/* Top progress strip — fills to indicate autoplay timer */}
      <div className="absolute top-0 left-0 right-0 z-20 h-1.5 bg-transparent">
        <div
          className="h-full bg-gold shadow-[0_0_8px_rgba(200,162,74,0.6)]"
          style={{ width: `${progress * 100}%` }}
        />
      </div>
    </div>
  );
}
